import { useInkind } from '@gik/api/inkinds/inkind';
import { translationKeys } from '@gik/calendar/components/Calendar/i18n/en';
import { CheckoutLauncher } from '@gik/checkout/components/CheckoutLauncher/CheckoutLauncher';
import {
  createCartItemFromProduct,
  productHasIntegratedCheckout,
  productIsGiftcard,
} from '@gik/checkout/utils/productUtils';
import type { CartItem } from '@gik/core/models/gik/Order';
import type { Product } from '@gik/core/models/gik/Product';
import routes from '@gik/core/routes';
import { Debug } from '@gik/core/utils/asPlaceholderReactNode';
import { navigateTo } from '@gik/core/utils/navigateTo';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import type { ProductTileDirection } from '@gik/ui-legacy/gik/ProductTile';
import { ProductTile } from '@gik/ui-legacy/gik/ProductTile';
import { UI } from '@gik/ui-legacy/UIManager';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IWishlistProductTileProps {
  item: Product;
  inkindRouteId: string;
  toolbar: React.ReactNode;
  direction?: ProductTileDirection;
}

function WishlistProductTileComp({ item, inkindRouteId, toolbar, direction = 'column' }: IWishlistProductTileProps) {
  const { data: inkind } = useInkind(inkindRouteId);

  const cartItem: CartItem = createCartItemFromProduct(item, inkind, true);
  const hasIntegratedCheckout: boolean = productHasIntegratedCheckout(item);

  const isSamplePage = useInkindStore(state => state.isSamplePage);
  const { t } = useTranslation();

  const wishlistProductUrl = !hasIntegratedCheckout
    ? `${routes.products}/${item?.slug}?routeId=${inkindRouteId}`
    : null;

  return (
    <>
      <CheckoutLauncher
        key={item.id}
        initiatedOn="wishlist"
        cart={[cartItem]}
        cachedProducts={[item]}
        initialInkindRouteId={inkindRouteId}
        child={onClick => (
          <ProductTile
            containImage={productIsGiftcard(item)}
            imageUrl={item.gridImage}
            onClick={async event => {
              if (isSamplePage) {
                return await UI.alert(t(translationKeys.samplePageWarning).toString());
              }
              return onClick ? onClick(event) : navigateTo(wishlistProductUrl);
            }}
            toolbar={toolbar}
            direction={direction}
            title={item.name}
          />
        )}
      />
      <Debug data={item.checkoutType} />
    </>
  );
}

export const WishlistProductTile = withComponentErrorBoundary(
  React.memo(WishlistProductTileComp, (prevProps, nextProps) => prevProps.toolbar === nextProps.toolbar)
);
